import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save, RefreshCw } from 'lucide-react';
import { logger } from '@/lib/logger';

interface IntegrationSetting {
  id: string;
  integration_name: string;
  display_name: string;
  base_url: string | null;
  api_host: string | null;
  is_active: boolean;
  description: string | null;
  metadata: any;
}

export function IntegrationSettingsManager() {
  const { toast } = useToast();
  const [settings, setSettings] = useState<IntegrationSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{
    base_url: string;
    api_host: string;
  }>({ base_url: '', api_host: '' });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('integration_settings')
        .select('*')
        .eq('integration_name', 'rapidapi_zillow')
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setSettings([data]);
      } else {
        // No setting found, show empty state or default
        setSettings([]);
      }
    } catch (error: any) {
      logger.error('Error loading integration settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load integration settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (setting: IntegrationSetting) => {
    setEditing(setting.id);
    setEditValues({
      base_url: setting.base_url || '',
      api_host: setting.api_host || '',
    });
  };

  const cancelEditing = () => {
    setEditing(null);
    setEditValues({ base_url: '', api_host: '' });
  };

  const saveSetting = async (settingId: string) => {
    try {
      setSaving(settingId);
      
      const { data, error } = await supabase
        .from('integration_settings')
        .update({
          base_url: editValues.base_url || null,
          api_host: editValues.api_host || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', settingId)
        .select()
        .single();

      if (error) throw error;

      toast({
        title: 'Success',
        description: 'Integration setting updated successfully',
      });

      setEditing(null);
      await loadSettings();
      
      // Clear cache on the server side by calling a function if needed
      // For now, the cache will expire naturally in 5 minutes
    } catch (error: any) {
      logger.error('Error saving integration setting:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save integration setting',
        variant: 'destructive',
      });
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const rapidApiSetting = settings.find(s => s.integration_name === 'rapidapi_zillow');

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              RapidAPI (Zillow Data) Configuration
              {rapidApiSetting?.is_active && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  Active
                </Badge>
              )}
            </CardTitle>
            <CardDescription>
              Configure the RapidAPI endpoint for Zillow property data. The system will use the new migrated endpoint by default.
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadSettings}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {rapidApiSetting ? (
          <div className="space-y-4">
            {editing === rapidApiSetting.id ? (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
                <div className="space-y-2">
                  <Label htmlFor="base_url">Base URL</Label>
                  <Input
                    id="base_url"
                    value={editValues.base_url}
                    onChange={(e) =>
                      setEditValues({ ...editValues, base_url: e.target.value })
                    }
                    placeholder="https://us-housing-market-data1.p.rapidapi.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    The base URL for the RapidAPI endpoint (without trailing slash)
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="api_host">API Host</Label>
                  <Input
                    id="api_host"
                    value={editValues.api_host}
                    onChange={(e) =>
                      setEditValues({ ...editValues, api_host: e.target.value })
                    }
                    placeholder="us-housing-market-data1.p.rapidapi.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    The value for the x-rapidapi-host header
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => saveSetting(rapidApiSetting.id)}
                    disabled={saving === rapidApiSetting.id}
                    size="sm"
                  >
                    {saving === rapidApiSetting.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={cancelEditing}
                    disabled={saving === rapidApiSetting.id}
                    size="sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Base URL</Label>
                    <p className="text-sm font-mono mt-1 break-all">
                      {rapidApiSetting.base_url || 'Not configured'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">API Host</Label>
                    <p className="text-sm font-mono mt-1 break-all">
                      {rapidApiSetting.api_host || 'Not configured'}
                    </p>
                  </div>
                </div>
                {rapidApiSetting.description && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Description</Label>
                    <p className="text-sm mt-1">{rapidApiSetting.description}</p>
                  </div>
                )}
                <Button
                  variant="outline"
                  onClick={() => startEditing(rapidApiSetting)}
                  size="sm"
                >
                  Edit Configuration
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <p>Integration setting not found in database.</p>
            <p className="text-xs mt-2">
              The system will use the default migrated endpoint: us-housing-market-data1.p.rapidapi.com
            </p>
          </div>
        )}

        <div className="pt-4 border-t">
          <p className="text-xs text-muted-foreground">
            <strong>Note:</strong> Changes to the endpoint configuration may take up to 5 minutes to take effect
            due to caching. The cache will automatically refresh.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}

